import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Navbar as BSNavbar, Container, Nav, NavDropdown, Badge } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { FiUser, FiLogOut, FiHome, FiMessageSquare, FiSettings, FiHeart, FiMap, FiList } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { UserAvatar } from './OptimizedImage';
import api from '../services/api';

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
      // Poll for new messages every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    if (isHomePage) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    } else {
      setScrolled(true);
    }
  }, [isHomePage]);

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get('/api/messages/unread-count');
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navbarStyle = {
    backgroundColor: isHomePage && !scrolled ? 'transparent' : 'white',
    transition: 'all 0.3s ease',
    boxShadow: scrolled ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
  };

  const linkStyle = isHomePage && !scrolled ? { color: 'white' } : {};

  return (
    <BSNavbar expand="lg" fixed="top" className="py-2" style={navbarStyle}>
      <Container fluid style={{ maxWidth: '1400px' }}>
        <BSNavbar.Brand as={Link} to="/" className="d-flex align-items-center">
          <img
            src="/logos/Prancheta 1_2.svg"
            alt="Nukki"
            height="45"
            className="d-inline-block align-top"
            style={{
              filter: isHomePage && !scrolled ? 'brightness(0) invert(1)' : 'none',
              transition: 'filter 0.3s ease'
            }}
          />
        </BSNavbar.Brand>

        <BSNavbar.Toggle
          aria-controls="navbar-nav"
          style={{
            borderColor: isHomePage && !scrolled ? 'white' : 'rgba(0,0,0,.1)',
            filter: isHomePage && !scrolled ? 'brightness(0) invert(1)' : 'none'
          }}
        />

        <BSNavbar.Collapse id="navbar-nav">
          <Nav className="ms-auto align-items-lg-center">
            <Nav.Link as={Link} to="/properties" className="fw-medium px-3 d-flex align-items-center gap-1" style={linkStyle}>
              <FiList /> {t('navbar.explore')}
            </Nav.Link>

            <Nav.Link as={Link} to="/map" className="fw-medium px-3 d-flex align-items-center gap-1" style={linkStyle}>
              <FiMap /> {t('navbar.map', 'Mapa')}
            </Nav.Link>

            <Nav.Link as={Link} to="/favorites" className="fw-medium px-3 d-flex align-items-center gap-1" style={linkStyle}>
              <FiHeart /> {t('navbar.favorites')}
            </Nav.Link>

            {isAuthenticated ? (
              <>
                <Nav.Link as={Link} to="/messages" className="fw-medium px-3 position-relative d-flex align-items-center gap-1" style={linkStyle}>
                  <FiMessageSquare /> {t('navbar.messages')}
                  {unreadCount > 0 && (
                    <Badge bg="primary" pill className="ms-1">
                      {unreadCount}
                    </Badge>
                  )}
                </Nav.Link>

                {user?.is_host && (
                  <Nav.Link as={Link} to="/host/dashboard" className="fw-medium px-3" style={linkStyle}>
                    {t('navbar.hostDashboard')}
                  </Nav.Link>
                )}

                {user?.is_owner && (
                  <Nav.Link as={Link} to="/owner/dashboard" className="fw-medium px-3" style={linkStyle}>
                    {t('navbar.ownerDashboard', 'Painel do Proprietário')}
                  </Nav.Link>
                )}

                {user?.is_admin && (
                  <Nav.Link
                    as={Link}
                    to="/admin/dashboard"
                    className="fw-semibold px-3 d-flex align-items-center gap-1"
                    style={isHomePage && !scrolled ? { color: 'white' } : { color: '#ff6b6b' }}
                  >
                    <FiSettings /> {t('admin.title')}
                  </Nav.Link>
                )}

                <NavDropdown
                  title={
                    <div className="d-inline-flex align-items-center gap-2">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center overflow-hidden"
                        style={{
                          width: '32px',
                          height: '32px',
                          background: 'var(--nukki-secondary)',
                          color: 'white'
                        }}
                      >
                        {user?.avatar ? (
                          <UserAvatar
                            src={user.avatar}
                            alt={user.first_name}
                            size={32}
                          />
                        ) : (
                          <FiUser />
                        )}
                      </div>
                    </div>
                  }
                  id="user-dropdown"
                  align="end"
                  className="px-2"
                >
                  <NavDropdown.Item as={Link} to="/dashboard">
                    <FiHome className="me-2" /> {t('navbar.dashboard')}
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/profile">
                    <FiUser className="me-2" /> {t('navbar.profile')}
                  </NavDropdown.Item>
                  {!user?.is_host && (
                    <NavDropdown.Item as={Link} to="/host/dashboard">
                      <FiHome className="me-2" /> {t('navbar.becomeHost')}
                    </NavDropdown.Item>
                  )}
                  {user?.is_owner && (
                    <NavDropdown.Item as={Link} to="/owner/dashboard">
                      <FiHome className="me-2" /> {t('navbar.ownerDashboard', 'Painel do Proprietário')}
                    </NavDropdown.Item>
                  )}
                  {user?.is_admin && (
                    <>
                      <NavDropdown.Divider />
                      <NavDropdown.Item
                        as={Link}
                        to="/admin/dashboard"
                        style={{ color: '#ff6b6b', fontWeight: '600' }}
                      >
                        <FiSettings className="me-2" /> {t('admin.panel')}
                      </NavDropdown.Item>
                    </>
                  )}
                  <NavDropdown.Divider />
                  <NavDropdown.Item onClick={handleLogout}>
                    <FiLogOut className="me-2" /> {t('navbar.logout')}
                  </NavDropdown.Item>
                </NavDropdown>
              </>
            ) : (
              <div className="d-flex gap-2 mt-2 mt-lg-0 ms-lg-3">
                <Link
                  to="/login"
                  className="btn btn-sm"
                  style={isHomePage && !scrolled ? {
                    border: 'none',
                    color: 'white',
                    backgroundColor: 'transparent'
                  } : {
                    border: 'none',
                    color: '#6c757d',
                    backgroundColor: 'transparent'
                  }}
                >
                  {t('navbar.login')}
                </Link>
                <Link
                  to="/register"
                  className="btn btn-primary btn-sm"
                  style={{ border: 'none' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#254444'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                >
                  {t('navbar.signUp')}
                </Link>
              </div>
            )}
          </Nav>
        </BSNavbar.Collapse>
      </Container>
    </BSNavbar>
  );
};

export default Navbar;
